import React, { useMemo } from 'react';
import { Space } from 'antd';
import { useRouter } from 'next/router';
import { UserRoleEnum, useLoginUserMutation } from '@vette/data-access';
import { Title, Text } from '../../atoms';
import { AuthForm } from '../AuthForm';
import { useAppContext } from '@vette/frontend-utils';

type Props = {
  redirectPath: string;
};

export const Login: React.FC<Props> = ({ redirectPath }) => {
  const router = useRouter();
  const [loginUser] = useLoginUserMutation({ errorPolicy: 'ignore' });

  const onSuccess = async () => {
    await loginUser();
    router.replace(redirectPath);
  };

  const appContext = useAppContext();
  const appName = appContext.app;

  const { loginSubtitle, userNotFoundErrorMessage, userRole } = useMemo(() => {
    let loginSubtitle: string;
    let userNotFoundErrorMessage: string;
    let userRole: UserRoleEnum;

    switch (appName) {
      case 'Admin': {
        loginSubtitle = 'Please log in to your account.';
        userNotFoundErrorMessage =
          'We cannot find your account. Are you registered as admin?';
        userRole = UserRoleEnum.ADMIN;
        break;
      }
      case 'Client': {
        loginSubtitle = 'Please log in to your account.';
        userNotFoundErrorMessage =
          'We cannot find your account. Are you registered as client / company interviewer?';
        userRole = UserRoleEnum.CLIENT;
        break;
      }
      case 'Vetter': {
        loginSubtitle = 'Please log in to your account and happy Vetting!';
        userNotFoundErrorMessage =
          'We cannot find your account. Are you registered as external Vetter? If you are a new Vetter, please register.';
        userRole = UserRoleEnum.VETTER;
        break;
      }
      default: {
        throw new Error('Unhandled case');
      }
    }

    return {
      loginSubtitle,
      userNotFoundErrorMessage,
      userRole,
    };
  }, [appName]);

  return (
    <AuthForm
      flow="login"
      userRole={userRole}
      onSuccess={onSuccess}
      userNotFoundErrorMessage={userNotFoundErrorMessage}
      header={
        <Space direction="vertical" size={12}>
          <Title level={4}>Welcome back!</Title>
          <Text $size={15}>{loginSubtitle}</Text>
        </Space>
      }
    />
  );
};
